import { Injectable } from '@angular/core';
import { PlessHttp } from 'src/app/shared/http/pless-http.service';
import { PlessRequest } from 'src/app/shared/vo/pless-request.model';
import { PlessJsonResponderSub } from 'src/app/commons/kymw00/pless-json-responder-sub.service';
import { PlessDialogServiceSub } from 'src/app/commons/kymw00/pless-dialog-service-sub';
import { KYMW7401Model } from '../kymw7401.model';
import { KYMW0300OutputModel } from '../../../../commons/kymw03/kymw0300/vo/kymw0300-output.model';

import type {
  GetCandidateGoodsListParameter,
  GetCandidateGoodsListCombinationPcodeList,
  GetCandidateGoodsListCombinationItemCodeList,
  GetCandidateGoodsListCombinationTagIdList,
  GetCandidateGoodsListCombinationGoodsMarkCodeList,
  GetCandidateGoodsListCombinationCoopCodeList,
  GetCandidateGoodsListCombinationDutiesList,
  GetCandidateGoodsListCombinationGuideGoodsNameList,
  GetCandidateGoodsListCombinationGuideStandardNameList,
} from '../vo/get-candidate-goods-list.model';

// リクエスト構造
export interface GetCandidateGoodsListRequest {
  search: {
    getCandidateGoodsList: {
      parameter: GetCandidateGoodsListParameter;
    };
    PcodeList?: {
      combination: {
        parameter: GetCandidateGoodsListCombinationPcodeList;
      } [];
    };
    ItemCodeList?: {
      combination: {
        parameter: GetCandidateGoodsListCombinationItemCodeList;
      } [];
    };
    TagIdList?: {
      combination: {
        parameter: GetCandidateGoodsListCombinationTagIdList;
      } [];
    };
    GoodsMarkCodeList?: {
      combination: {
        parameter: GetCandidateGoodsListCombinationGoodsMarkCodeList;
      } [];
    };
    CoopCodeList?: {
      combination: {
        parameter: GetCandidateGoodsListCombinationCoopCodeList;
      } [];
    };
    DutiesList?: {
      combination: {
        parameter: GetCandidateGoodsListCombinationDutiesList;
      } [];
    };
    GuideGoodsNameList?: {
      combination: {
        parameter: GetCandidateGoodsListCombinationGuideGoodsNameList;
      } [];
    };
    GuideStandardNameList?: {
      combination: {
        parameter: GetCandidateGoodsListCombinationGuideStandardNameList;
      } [];
    };
  };
}

// レスポンス構造
export interface GetCandidateGoodsListResponse {
  /** 商品候補一覧 */
  CandidateGoodsList: {
    /** アイテムコード */
    ItemCode: string;
    /** Ｐコード */
    PCode: string;
    /** 部門コード */
    ClassCode: string;
    /** 部門名 */
    ClassName: string;
    /** バイヤーコード */
    BuyerCode: string;
    /** バイヤー名 */
    BuyerName: string;
    /** 商品名 */
    GuideGoodsName: string;
    /** 規格名 */
    GuideStandardName: string;
    /** メーカー名 */
    MakerName: string;
    /** 支払先取引先コード */
    PayeeClientCode: string;
    /** 支払先取引先名 */
    PayeeClientKanji: string;
    /** 本登録日時 (YYYY年MM月DD日 形式) */
    RegularRegistDate: string;
  }[];
}

/** 商品候補一覧取得 サービス */
@Injectable()
export class GetCandidateGoodsListService extends PlessJsonResponderSub<GetCandidateGoodsListResponse> {
  model: KYMW7401Model;
  private requestData: GetCandidateGoodsListRequest;
  private successFunc: (response: GetCandidateGoodsListResponse) => void;

  constructor(
    protected plessHttp: PlessHttp,
    protected dialogService: PlessDialogServiceSub
  ) {
    super(plessHttp, dialogService);
  }

  /**
   * bodyにセットする値を返す
   *
   * @returns {PlessRequest} リクエストJSON
   */
  private getBody(request: KYMW0300OutputModel): PlessRequest {
    // 検索条件
    const parameter: GetCandidateGoodsListParameter = {
      Pcode: request.pcode ?? '',
      ItemCode: request.itemCode ?? '',
      JANCode: request.janCode ?? '',
      MakerName: request.makerName ?? '',
      TagSearchType: request.tagSearchType ?? '',
      GoodsMarkSearchType: request.goodsMarkSearchType ?? '',
      DataKbnFlg: request.dataKbnFlg ?? false,
      GoodsDiv: request.goodsDiv ?? '',
      BuyerCode: request.buyerCode ?? '',
      AdmitStatus: request.admitStatus ?? '',
      RegistDateFrom: request.registDateFrom ?? '',
      RegistDateTo: request.registDateTo ?? '',
      UpdateDateFrom: request.updateDateFrom ?? '',
      UpdateDateTo: request.updateDateTo ?? '',
      ClassCode: request.classCode ?? '',
      MiddleClassCode: request.middleClassCode ?? '',
      SmallClassCode: request.smallClassCode ?? '',
      OrderClientCode: request.ordrClientCode ?? '',
      OrderClientKanji: request.ordrClientKanji ?? '',
      PayeeClientCode: request.payeeClientCode ?? '',
      PayeeClientKanji: request.payeeClientKanji ?? '',
    };

    // リクエストのベースを作成
    this.requestData = {
      search: {
        getCandidateGoodsList: { parameter },
      }
    };

    // 以下、リスト検索（有効な配列データが 1 件以上存在した場合のみセットする）
    const combinationPCodeList = [] as { parameter: GetCandidateGoodsListCombinationPcodeList }[];
    const pCodeList = request.pcodeList;
    if (pCodeList && Array.isArray(pCodeList)) {
      for (const pCode of pCodeList) {
        if (pCode) {
          combinationPCodeList.push({ parameter: { Pcode: pCode } });
        }
      }
    }
    if (combinationPCodeList.length > 0) {
      this.requestData.search.PcodeList = {
        combination: combinationPCodeList
      }
    }

    const combinationItemCodeList = [] as { parameter: GetCandidateGoodsListCombinationItemCodeList }[];
    const itemCodeList = request.itemcodeList;
    if (itemCodeList && Array.isArray(itemCodeList)) {
      for (const itemCode of itemCodeList) {
        if (itemCode) {
          combinationItemCodeList.push({ parameter: { ItemCode: itemCode } });
        }
      }
    }
    if (combinationItemCodeList.length > 0) {
      this.requestData.search.ItemCodeList = {
        combination: combinationItemCodeList
      }
    }

    const combinationTagIdList = [] as { parameter: GetCandidateGoodsListCombinationTagIdList }[];
    const tagIdList = request.tagId;
    if (tagIdList && Array.isArray(tagIdList)) {
      for (const tagId of tagIdList) {
        if (tagId) {
          combinationTagIdList.push({ parameter: { TagId: tagId } });
        }
      }
    }
    if (combinationTagIdList.length > 0) {
      this.requestData.search.TagIdList = {
        combination: combinationTagIdList
      }
    }

    const combinationGoodsMarkCodeList = [] as { parameter: GetCandidateGoodsListCombinationGoodsMarkCodeList }[];
    const goodsMarkList = request.goodsMark;
    if (goodsMarkList && Array.isArray(goodsMarkList)) {
      for (const goodsMark of goodsMarkList) {
        if (goodsMark) {
          combinationGoodsMarkCodeList.push({ parameter: { GoodsMarkCode: goodsMark } });
        }
      }
    }
    if (combinationGoodsMarkCodeList.length > 0) {
      this.requestData.search.GoodsMarkCodeList = {
        combination: combinationGoodsMarkCodeList
      }
    }

    const combinationCoopCodeList = [] as { parameter: GetCandidateGoodsListCombinationCoopCodeList }[];
    const coopCodeList = request.coopCodeList;
    if (coopCodeList && Array.isArray(coopCodeList)) {
      for (const coopCode of coopCodeList) {
        if (coopCode) {
          combinationCoopCodeList.push({ parameter: { CoopCode: coopCode } });
        }
      }
    }
    if (combinationCoopCodeList.length > 0) {
      this.requestData.search.CoopCodeList = {
        combination: combinationCoopCodeList
      }
    }

    const combinationDutiesList = [] as { parameter: GetCandidateGoodsListCombinationDutiesList }[];
    const dutiesList = request.duties;
    if (dutiesList && Array.isArray(dutiesList)) {
      for (const duties of dutiesList) {
        if (duties) {
          combinationDutiesList.push({ parameter: { Duties: duties } });
        }
      }
    }
    if (combinationDutiesList.length > 0) {
      this.requestData.search.DutiesList = {
        combination: combinationDutiesList
      }
    }

    const combinationGuideGoodsNameList = [] as { parameter: GetCandidateGoodsListCombinationGuideGoodsNameList }[];
    const guideGoodsNameList = request.guideGoodsNameList;
    if (guideGoodsNameList && Array.isArray(guideGoodsNameList)) {
      for (const guideGoodsName of guideGoodsNameList) {
        if (guideGoodsName) {
          combinationGuideGoodsNameList.push({ parameter: { GuideGoodsName: guideGoodsName } });
        }
      }
    }
    if (combinationGuideGoodsNameList.length > 0) {
      this.requestData.search.GuideGoodsNameList = {
        combination: combinationGuideGoodsNameList
      }
    }

    const combinationGuideStandardNameList = [] as { parameter: GetCandidateGoodsListCombinationGuideStandardNameList }[];
    const guideStandardNameList = request.guideStandardNameList;
    if (guideStandardNameList && Array.isArray(guideStandardNameList)) {
      for (const guideStandardName of guideStandardNameList) {
        if (guideStandardName) {
          combinationGuideStandardNameList.push({ parameter: { GuideStandardName: guideStandardName } });
        }
      }
    }
    if (combinationGuideStandardNameList.length > 0) {
      this.requestData.search.GuideStandardNameList = {
        combination: combinationGuideStandardNameList
      }
    }

    return new PlessRequest(this.requestData);
  }

  /**
   * API呼び出し
   *
   * @param model
   * @memberof GetEbaseCandidateListService
   */
  execute(model: KYMW7401Model, request: KYMW0300OutputModel, func?: (response: GetCandidateGoodsListResponse) => void): void {
    this.model = model;
    if (func) {
      this.successFunc = func;
    }
    // ID: "getCandidateGoodsList" が既存と重複のため, エンドポイントにプレフィックスを追加した値とする
    this.send('KYMW74getCandidateGoodsList', this.getBody(request));
  }

  /**
   * API通信成功時の処理
   *
   * @param {*} data レスポンスデータ
   * @memberof GetEbaseCandidateListService
   */
  success(data: GetCandidateGoodsListResponse) {
    if (this.successFunc) {
      this.successFunc(data);
    }
  }

}
